import { createRouter, createWebHistory } from 'vue-router'
import { authService } from '@/api/services/authService'
import { env } from '@/config/env'

// Lazy load all route components for better code splitting
const HomeView = () => import('../views/HomeView.vue')
const NotFoundView = () => import('../views/NotFoundView.vue')

// Composer module - lazy loaded as a separate chunk
const CreateSubscriptionView = () => import('@/views/composer/CreateSubscriptionView.vue')
const EditSubscriptionView = () => import('@/views/composer/EditSubscriptionView.vue')

// Subscription Management module - lazy loaded as a separate chunk  
const SubscriptionsView = () => import('@/views/subscription-management/SubscriptionsView.vue')

// Administration module - lazy loaded as a separate chunk
const AdminView = () => import('@/views/administration/AdminView.vue')

export function createAppRouter() {
  const router = createRouter({
    history: createWebHistory(env.BASE_PATH),
    routes: [
      {
        path: '/',
        name: 'home',
        component: HomeView,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/composer',
        name: 'composer',
        redirect: '/composer/create-subscription',
        meta: {
          requiresAuth: true,
          requiresCompose: true,
        },
        children: [
          {
            path: 'create-subscription',
            name: 'composer-create-subscription',
            component: CreateSubscriptionView,
            meta: {
              requiresAuth: true,
              requiresCompose: true,
            },
          },
          {
            path: 'edit-subscription',
            name: 'composer-edit-subscription',
            component: EditSubscriptionView,
            meta: {
              requiresAuth: true,
              requiresCompose: true,
            },
          },
        ],
      },
      {
        path: '/subscription-management',
        name: 'subscription-management',
        redirect: '/subscription-management/subscriptions',
        meta: {
          requiresAuth: true,
          requiresApprove: true,
        },
        children: [
          {
            path: 'subscriptions',
            name: 'subscription-management-subscriptions',
            component: SubscriptionsView,
            meta: {
              requiresAuth: true,
              requiresApprove: true,
            },
          },
        ],
      },
      {
        path: '/administration',
        name: 'administration',
        redirect: '/administration/approvers',
        meta: {
          requiresAuth: true,
          requiresApprove: true,
        },
        children: [
          {
            path: 'approvers',
            name: 'administration-approvers',
            component: AdminView,
            meta: {
              requiresAuth: true,
              requiresApprove: true,
            },
          },
          {
            path: 'seals',
            name: 'administration-seals',
            component: AdminView,
            meta: {
              requiresAuth: true,
              requiresApprove: true,
            },
          },
          {
            path: 'documents',
            name: 'administration-documents',
            component: AdminView,
            meta: {
              requiresAuth: true,
              requiresApprove: true,
            },
          },
        ],
      },
      {
        path: '/not-found',
        name: 'not-found',
        component: NotFoundView,
      },
    ],
  })

  // Navigation guard for protected routes
  router.beforeEach(async (to) => {
    if (to.meta.requiresAuth) {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()

      // Check if we have a cache key and need to authenticate
      const cacheKey = authService.getCacheKeyFromUrl()

      if (cacheKey && !authStore.isAuthenticated && !authStore.isLoading) {
        try {
          await authService.authenticateWithCacheKey(cacheKey)
        } catch {
          // Authentication failed, redirect to not-found
          return { name: 'not-found' }
        }
      }

      // Check authentication status using the store directly
      if (!authStore.isAuthenticated) {
        return { name: 'not-found' }
      }

      if (to.meta.requiresCompose && !authStore.canCompose) {
        return { name: 'not-found' }
      }

      if (to.meta.requiresApprove && !authStore.canApprove) {
        return { name: 'not-found' }
      }
    }
  })

  return router
}
